import type { ReactNode } from "react";
import { LegendList } from "@legendapp/list/react";
import { cn } from "@/lib/utils";

/** Above this count, mount rows through Legend List to keep topic/space filter clicks responsive. */
export const LIBRARY_DOCUMENT_VIRTUALIZE_THRESHOLD = 40;

type LibraryDocumentsVirtualListProps<T extends { id: string }> = {
  items: T[];
  renderItem: (item: T) => ReactNode;
  className?: string;
  estimatedItemSize?: number;
};

export function LibraryDocumentsVirtualList<T extends { id: string }>({
  items,
  renderItem,
  className,
  estimatedItemSize = 52,
}: LibraryDocumentsVirtualListProps<T>) {
  if (items.length <= LIBRARY_DOCUMENT_VIRTUALIZE_THRESHOLD) {
    return (
      <div className={cn("space-y-0.5 overflow-x-hidden w-full", className)}>
        {items.map((item) => (
          <div key={item.id}>{renderItem(item)}</div>
        ))}
      </div>
    );
  }

  return (
    <LegendList<T>
      className={cn(
        "min-h-[120px] max-h-[min(70vh,720px)] overflow-x-hidden w-full",
        className
      )}
      data={items}
      keyExtractor={(item) => item.id}
      estimatedItemSize={estimatedItemSize}
      getEstimatedItemSize={() => estimatedItemSize}
      recycleItems={false}
      drawDistance={320}
      renderItem={({ item }) => <div className="pb-0.5">{renderItem(item)}</div>}
    />
  );
}
